<template>
  <div class="page-container">
    <div class="page-content">
    <div class="header mb-6">
      <div class="flex justify-between items-center">
        <div>
          <h1 class="text-2xl font-bold text-gray-900">{{ t('products.title') }}</h1>
          <p class="text-gray-600">{{ t('products.subtitle') }}</p>
        </div>
        <div class="flex gap-3">
          <button
            @click="refreshProducts"
            :disabled="loading"
            class="btn btn-secondary"
          >
            <span v-if="loading" class="animate-spin">⟳</span>
            {{ t('common.refresh') }}
          </button>
          <router-link to="/products/new" class="btn btn-primary">
            {{ t('products.addProduct') }}
          </router-link>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters mb-6 p-4 bg-gray-50 rounded-lg">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">{{ t('common.search') }}</label>
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="t('products.searchPlaceholder')"
            class="input w-full"
            @input="debouncedSearch"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">{{ t('products.labelCategory') }}</label>
          <select v-model="selectedCategory" class="select w-full">
            <option value="">{{ t('products.allCategories') }}</option>
            <option v-for="category in categories" :key="category" :value="category">
              {{ category }}
            </option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">{{ t('common.status') }}</label>
          <select v-model="statusFilter" class="select w-full">
            <option value="all">{{ t('products.allProducts') }}</option>
            <option value="active">{{ t('products.activeOnly') }}</option>
            <option value="inactive">{{ t('products.inactiveOnly') }}</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Error Alert -->
    <div v-if="error" class="alert alert-error mb-4">
      <span>{{ error }}</span>
      <button @click="clearError" class="btn btn-sm btn-ghost">×</button>
    </div>

    <!-- Products Table -->
    <div class="card">
      <DataTable
        :columns="columns"
        :data="filteredProducts"
        :loading="loading"
        :searchable="false"
        @sort="handleSort"
      >
        <template #actions="{ item }">
          <div class="flex gap-2">
            <router-link
              :to="`/products/${item.id}`"
              class="btn btn-sm btn-ghost"
            >
              {{ t('common.view') }}
            </router-link>
            <router-link
              :to="`/products/${item.id}/edit`"
              class="btn btn-sm btn-ghost"
            >
              {{ t('common.edit') }}
            </router-link>
            <button
              @click="toggleProductStatus(item)"
              :class="[
                'btn btn-sm',
                item.isActive ? 'btn-warning' : 'btn-success'
              ]"
            >
              {{ item.isActive ? t('common.deactivate') : t('common.activate') }}
            </button>
          </div>
        </template>

        <template #status="{ item }">
          <span
            :class="[
              'badge',
              item.isActive ? 'badge-success' : 'badge-error'
            ]"
          >
            {{ item.isActive ? t('common.active') : t('common.inactive') }}
          </span>
        </template>

        <template #cost="{ item }">
          <span v-if="item.cost" class="font-mono">
            {{ item.cost.currency === 'USD' ? '$' : item.cost.currency }}{{ item.cost.amount.toFixed(2) }}
          </span>
          <span v-else class="text-gray-400">—</span>
        </template>

        <template #price="{ item }">
          <span v-if="item.price" class="font-mono">
            {{ item.price.currency === 'USD' ? '$' : item.price.currency }}{{ item.price.amount.toFixed(2) }}
          </span>
          <span v-else class="text-gray-400">—</span>
        </template>
      </DataTable>
    </div>

    <!-- Empty State -->
    <div v-if="!loading && filteredProducts.length === 0" class="text-center py-12">
      <div class="text-gray-400 text-6xl mb-4">📦</div>
      <h3 class="text-lg font-medium text-gray-900 mb-2">{{ t('products.noProductsFound') }}</h3>
      <p class="text-gray-500 mb-4">
        {{ searchQuery ? t('products.noProductsMatch') : t('products.getStarted') }}
      </p>
      <router-link to="/products/new" class="btn btn-primary">
        {{ t('products.addFirstProduct') }}
      </router-link>
    </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useProductStore } from '../../stores/useProductStore';
// @ts-ignore — packages/ui excluded from tsconfig.app.json
import DataTable from '../../../packages/ui/src/components/DataTable.vue';
// @ts-ignore
import type { Column } from '../../../packages/ui/src/types';
import { debounce } from 'lodash-es';

const { t } = useI18n();
const productStore = useProductStore();

const searchQuery = ref('');
const selectedCategory = ref('');
const statusFilter = ref('all');

const { products, loading, error } = productStore;

const categories = computed(() => {
  const cats = new Set(products.map((p: any) => p.category).filter(Boolean));
  return Array.from(cats).sort();
});

const filteredProducts = computed(() => {
  let filtered = products;

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter((p: any) =>
      p.name.toLowerCase().includes(query) ||
      p.sku.toLowerCase().includes(query) ||
      (p.barcode && p.barcode.toLowerCase().includes(query))
    );
  }

  if (selectedCategory.value) {
    filtered = filtered.filter((p: any) => p.category === selectedCategory.value);
  }

  if (statusFilter.value === 'active') {
    filtered = filtered.filter((p: any) => p.isActive);
  } else if (statusFilter.value === 'inactive') {
    filtered = filtered.filter((p: any) => !p.isActive);
  }

  return filtered;
});

const columns = computed<Column[]>(() => [
  { key: 'sku', label: t('products.colSku'), sortable: true },
  { key: 'name', label: t('products.colName'), sortable: true },
  { key: 'category', label: t('products.colCategory'), sortable: true },
  { key: 'barcode', label: t('products.colBarcode') },
  { key: 'cost', label: t('products.colCost'), sortable: true },
  { key: 'price', label: t('products.colPrice'), sortable: true },
  { key: 'status', label: t('common.status') },
  { key: 'actions', label: t('common.actions') }
]);

const debouncedSearch = debounce(() => {}, 300);

const handleSort = (key: string, direction: 'asc' | 'desc') => {
  console.log('Sort by:', key, direction);
};

const refreshProducts = async () => {
  await productStore.fetchProducts();
};

const toggleProductStatus = async (product: any) => {
  try {
    await productStore.updateProduct(product.id, { isActive: !product.isActive });
  } catch (error) {
    console.error('Error toggling product status:', error);
  }
};

const clearError = () => {
  productStore.clearError();
};

onMounted(async () => {
  await productStore.fetchProducts();
});

watch([selectedCategory, statusFilter], () => {});
</script>
